import { deepObjectFreeze } from 'utils/deep-object-freeze/deep-object-freeze';
import {
  getTestableFeaturesEnabled,
  getQueryParams,
} from '../../utils/testable/testable.helpers';

/*
 When adding a new language, please make sure you add it in all required places
 And perform the necessary checks
 See the Confluence page for more information about changes to Storybook, Hermes and Zeus
 https://workivate.atlassian.net/wiki/spaces/WT/pages/4121755701
 */

// Add locales here that should appear on test only (hidden behind 'testable' flag).
const testableLocaleCodes: ReadonlyArray<string> = [];

// languages that are written right-to-left (RTL)
export const rtlLanguages: ReadonlyArray<string> = ['ar_001', 'he_IL', 'ur_PK'];

export const getIsRTL = (locale: string) => rtlLanguages.includes(locale);

type TestableLocales = Record<(typeof testableLocaleCodes)[number], string>;

const testableLocales = testableLocaleCodes.reduce((accumulator, value) => {
  accumulator[value] = value;
  return accumulator;
}, {} as TestableLocales);

export const LanguageLabels = deepObjectFreeze({
  en_GB: 'English (GB)', // English (GB)
  en_US: 'English (US)', // English (US)
  en_CA: 'English (Canada)', // English (Canada)
  en_AU: 'English (Australia)', // English (Australia)
  es_US: 'Español (US)', // Spanish (US)
  fr_CA: 'Français (Canada)', // French (Canada)
  af_ZA: 'Afrikaans', // Afrikaans
  am_ET: 'አማርኛ', // Amharic
  ar_001: 'العربية', // Arabic
  az_AZ: 'Azərbaycan', // Azerbaijani
  bn_BD: 'বাংলা', // Bengali (Bangladesh)
  bs_BA: 'Bosanski', // Bosnian (Bosnia and Herzegovina)
  bg_BG: 'български', // Bulgarian (Bulgaria)
  zh_CN: '简体中文', // Chinese Simplified
  zh_HK: '繁体中文', // Chinese Traditional
  hr_HR: 'hrvatski', // Croatian (Croatia)
  cs_CZ: 'Česká', // Czech
  da_DK: 'Dansk', // Danish
  nl_NL: 'Nederlands', // Dutch (Netherlands)
  et_EE: 'Eesti', // Estonian (Estonia)
  fi_FI: 'Suomi', // Finnish
  fr_FR: 'Français', // French (France)
  de_DE: 'Deutsch', // German (Germany)
  el_GR: 'Ελληνικά', // Greek (Greece)
  he_IL: 'עברית', // Hebrew
  hi_IN: 'हिंदी', // Hindi (India)
  hu_HU: 'Magyar', // Hungarian
  id_ID: 'Bahasa Indonesia', // Indonesian (Indonesia)
  it_IT: 'Italiano', // Italian (Italy)
  ja_JP: '日本語', // Japanese
  ko_KR: '한국어', // Korean
  lv_LV: 'Latviski', // Latvian (Latvia)
  lt_LT: 'Lietuvių', // Lithuanian (Lithuania)
  ms_MY: 'Bahasa Malaysia', // Malay
  mn_MN: 'монгол', // Mongolian
  ne_NP: 'नेपाली', // Nepali (Nepal)
  nb_NO: 'Norsk', // Norwegian
  pl_PL: 'Polski', // Polish
  pt_BR: 'Português (Brasil)', // Portuguese (Brazil)
  pt_PT: 'Português (Portugal)', // Portuguese (Portugal)
  ro_RO: 'Română', // Romanian (Romania)
  ru_RU: 'Русский', // Russian
  sr_RS: 'српски', // Serbian (Serbia)
  sk_SK: 'Slovenský', // Slovak
  sl_SI: 'Slovenščina', // Slovenian (Slovenia)
  so_SO: 'Soomaali', // Somali
  es_ES: 'Español', // Spanish (Spain)
  es_419: 'Español (América Latina)', // Spanish (Latin America)
  sw_KE: 'Kiswahili', // Swahili
  sv_SE: 'Svenska', // Swedish (Sweden)
  tl_PH: 'Tagalog', // Tagalog
  ta_IN: 'தமிழ்', // Tamil (India)
  th_TH: 'ไทย', // Thai
  tr_TR: 'Türkçe', // Turkish (Turkey)
  uk_UA: 'Українська', // Ukrainian
  ur_PK: 'اردو', // Urdu
  vi_VN: 'Tiếng Việt', // Vietnamese
  xh_ZA: 'IsiXhosa', // Xhosa
  zu_ZA: 'isiZulu', // Zulu
} as const);

export const SupportedLocales = deepObjectFreeze({
  en_GB: 'en_GB',
  en_US: 'en_US',
  en_CA: 'en_CA',
  en_AU: 'en_AU',
  es_US: 'es_US',
  fr_CA: 'fr_CA',
  af_ZA: 'af_ZA',
  am_ET: 'am_ET',
  ar_001: 'ar_001',
  az_AZ: 'az_AZ',
  bn_BD: 'bn_BD',
  bs_BA: 'bs_BA',
  bg_BG: 'bg_BG',
  zh_CN: 'zh_CN',
  zh_HK: 'zh_HK',
  hr_HR: 'hr_HR',
  cs_CZ: 'cs_CZ',
  da_DK: 'da_DK',
  nl_NL: 'nl_NL',
  et_EE: 'et_EE',
  fi_FI: 'fi_FI',
  fr_FR: 'fr_FR',
  de_DE: 'de_DE',
  el_GR: 'el_GR',
  he_IL: 'he_IL',
  hi_IN: 'hi_IN',
  hu_HU: 'hu_HU',
  id_ID: 'id_ID',
  it_IT: 'it_IT',
  ja_JP: 'ja_JP',
  ko_KR: 'ko_KR',
  lv_LV: 'lv_LV',
  lt_LT: 'lt_LT',
  ms_MY: 'ms_MY',
  mn_MN: 'mn_MN',
  ne_NP: 'ne_NP',
  nb_NO: 'nb_NO',
  pl_PL: 'pl_PL',
  pt_BR: 'pt_BR',
  pt_PT: 'pt_PT',
  ro_RO: 'ro_RO',
  ru_RU: 'ru_RU',
  sr_RS: 'sr_RS',
  sk_SK: 'sk_SK',
  sl_SI: 'sl_SI',
  so_SO: 'so_SO',
  es_ES: 'es_ES',
  es_419: 'es_419',
  sw_KE: 'sw_KE',
  sv_SE: 'sv_SE',
  tl_PH: 'tl_PH',
  ta_IN: 'ta_IN',
  th_TH: 'th_TH',
  tr_TR: 'tr_TR',
  uk_UA: 'uk_UA',
  ur_PK: 'ur_PK',
  vi_VN: 'vi_VN',
  xh_ZA: 'xh_ZA',
  zu_ZA: 'zu_ZA',
  ...(getTestableFeaturesEnabled(getQueryParams()) ? testableLocales : {}),
} as const);

export const defaultLocale = SupportedLocales.en_GB;

export const languageKeysRoot = 'lw_unified_identity_app';
